import React, { useEffect, useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getBackendUrl, getImageUrl } from '../../lib/utils';

const injectHeaderScripts = (htmlCode) => {
  if (!htmlCode) return;

  // Clear previous injections
  const oldTags = document.querySelectorAll('[data-injected-script="true"]');
  oldTags.forEach(el => el.remove());

  // Parse HTML
  const container = document.createElement('div');
  container.innerHTML = htmlCode;

  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = document.createElement(node.tagName);
      
      // Copy attributes
      Array.from(node.attributes).forEach(attr => {
        tag.setAttribute(attr.name, attr.value);
      });

      // Special handling for scripts
      if (node.tagName.toLowerCase() === 'script') {
        if (node.src) {
          tag.src = node.src;
          tag.async = true;
        } else {
          tag.textContent = node.textContent;
        }
      } else {
        tag.innerHTML = node.innerHTML;
      }

      tag.setAttribute('data-injected-script', 'true');
      document.head.appendChild(tag);
    }
  });
};

const getBrowserFingerprint = () => {
  const screen = window.screen;
  const navigator = window.navigator;
  const date = new Date();
  
  const rawString = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    date.getTimezoneOffset(),
    navigator.hardwareConcurrency || '',
    navigator.deviceMemory || ''
  ].join('###');
  
  let hash = 0;
  for (let i = 0; i < rawString.length; i++) {
    const char = rawString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'fp_' + Math.abs(hash).toString(16);
};

const generateUuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const Layout = () => {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  // Cookie Consent Banner States
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [cookieTab, setCookieTab] = useState('settings'); // 'settings' | 'policy'
  const [consentId, setConsentId] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytical: false,
    marketing: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const url = getBackendUrl();
        const { data } = await axios.get(`${url}/api/settings`);
        setSettings(data);
        
        // Sync preferred image format settings to localStorage
        if (data && data.image_converter_settings && data.image_converter_settings.format) {
          localStorage.setItem('target_image_format', data.image_converter_settings.format);
        } else {
          localStorage.setItem('target_image_format', 'webp');
        }

        if (data && data.header_scripts) {
          injectHeaderScripts(data.header_scripts);
        }
        if (data && data.popup_settings && data.popup_settings.enabled) {
          const popup = data.popup_settings;
          const key = popup.show_type === 'once'
            ? `popup_closed_${popup.title || 'default'}`
            : 'popup_closed_session';
          if (!sessionStorage.getItem(key)) {
            setShowPopup(true);
          }
        }
      } catch (e) {
        console.error("Error loading layout settings", e);
      }
    };
    fetchSettings();
  }, []);

  // Cookie Consent Checker
  useEffect(() => {
    if (settings && settings.cookie_settings && settings.cookie_settings.enabled) {
      const savedConsentId = localStorage.getItem('cookie_consent_id');
      const savedPreferences = localStorage.getItem('cookie_preferences');
      
      const fp = getBrowserFingerprint();
      setFingerprint(fp);

      if (!savedConsentId) {
        const newId = generateUuid();
        setConsentId(newId);
        setShowCookieBanner(true);
      } else {
        setConsentId(savedConsentId);
        if (savedPreferences) {
          try {
            setCookiePreferences(JSON.parse(savedPreferences));
          } catch (e) {
            console.error(e);
          }
        }
      }
    }
  }, [settings]);

  const submitConsent = async (status, preferences) => {
    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/cookie/consent`, {
        consent_id: consentId,
        consent_status: status,
        consents_data: preferences,
        fingerprint: fingerprint
      });
      
      localStorage.setItem('cookie_consent_id', consentId);
      localStorage.setItem('cookie_preferences', JSON.stringify(preferences));
      setShowCookieBanner(false);
      setShowCookieSettings(false);
    } catch (e) {
      console.error("Failed to save cookie consent log", e);
    }
  };

  const handleAcceptAllCookies = () => {
    const prefs = { necessary: true, analytical: true, marketing: true };
    setCookiePreferences(prefs);
    submitConsent('accepted_all', prefs);
  };

  const handleRejectAllCookies = () => {
    const prefs = { necessary: true, analytical: false, marketing: false };
    setCookiePreferences(prefs);
    submitConsent('rejected_all', prefs);
  };

  const handleSaveCookiePreferences = () => {
    submitConsent('custom', cookiePreferences);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (settings && settings.popup_settings) {
      const popup = settings.popup_settings;
      const key = popup.show_type === 'once'
        ? `popup_closed_${popup.title || 'default'}`
        : 'popup_closed_session';
      sessionStorage.setItem(key, 'true');
    }
  };

  // Update SEO Meta Tags, OpenGraph, and LocalBusiness Schema on settings or location change
  useEffect(() => {
    if (!settings) return;

    const seo = settings.seo_settings || {};
    const footer = settings.footer_settings || {};
    const contact = settings.contact_settings || {};

    // 1. Title
    let pageTitle = seo.title || "İSİLKOOP | Modern Üretim ve Ticaret Alanları";
    const pathname = location.pathname.replace(/\/$/, "");
    
    if (pathname.endsWith('/kurumsal/hakkimizda')) {
      pageTitle = `Hakkımızda | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/kurumsal/yonetim-kurulu')) {
      pageTitle = `Yönetim Kurulu | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/kurumsal/misyon-vizyon')) {
      pageTitle = `Misyon & Vizyon | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/kurumsal/kalite-politikasi')) {
      pageTitle = `Kalite Politikamız | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/kurumsal/insan-kaynaklari')) {
      pageTitle = `İnsan Kaynakları | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/haberler')) {
      pageTitle = `Haberler | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/galeri')) {
      pageTitle = `Galeri | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/iletisim')) {
      pageTitle = `İletişim | ${seo.title || 'İSİLKOOP'}`;
    } else if (pathname.endsWith('/proje')) {
      pageTitle = `Proje Detayı | ${seo.title || 'İSİLKOOP'}`;
    }
    
    document.title = pageTitle;

    // 2. Standard Metas
    const updateMetaTag = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content || "");
    };

    updateMetaTag('description', seo.description || "S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi. Bölgede iş dünyasının ihtiyaçlarına yönelik modern üretim ve ticaret alanları.");
    updateMetaTag('keywords', seo.keywords || "Silivri kooperatif, sanayi sitesi, iş merkezi, ticaret alanı, ikitelli kooperatif, üretim alanı");

    // 2b. Dynamic Favicon Link Tags (32x32, 64x64, 512x512, apple-touch-icon)
    const getAbsoluteUrl = (url) => {
      const resolved = getImageUrl(url);
      if (!resolved) return '';
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) {
        return resolved;
      }
      return window.location.origin + resolved;
    };

    const updateFaviconTag = (rel, sizes, href) => {
      let selector = `link[rel="${rel}"]`;
      if (sizes) selector += `[sizes="${sizes}"]`;
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        if (sizes) tag.setAttribute('sizes', sizes);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    if (seo.favicon_url) {
      const faviconFullUrl = getAbsoluteUrl(seo.favicon_url);
      updateFaviconTag('icon', '32x32', faviconFullUrl);
      updateFaviconTag('icon', '64x64', faviconFullUrl);
      updateFaviconTag('icon', '512x512', faviconFullUrl);
      updateFaviconTag('apple-touch-icon', '180x180', faviconFullUrl);
      updateFaviconTag('shortcut icon', null, faviconFullUrl);
    }

    // 3. OpenGraph Metas
    const updateOgTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content || "");
    };

    updateOgTag('og:title', pageTitle);
    updateOgTag('og:description', seo.description || "S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi.");
    updateOgTag('og:type', 'website');
    updateOgTag('og:url', window.location.href);

    const logoFullUrl = footer.logo_url 
      ? getAbsoluteUrl(footer.logo_url)
      : (window.location.origin + "/assets/images/logo/logo-dark.png");
    updateOgTag('og:image', logoFullUrl);

    // 4. LocalBusiness JSON-LD Schema
    const schemaId = 'local-business-schema';
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = schemaId;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }

    const telephone = footer.footer_phone || contact.phone || "+90 212 877 89 18";
    const email = footer.footer_email || contact.email || "info@isilkoop.com";
    const address = footer.footer_address_office || contact.address_office || "Osb. Mh. Dolapdere Sanayi Sitesi Dolapdere İş Merkezi No: 27 - 4 İkitelli / Başakşehir / İstanbul";

    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "S.S. İkitelli - Silivri Toplu İşyeri Yapı Kooperatifi",
      "image": logoFullUrl,
      "@id": window.location.origin + "/",
      "url": window.location.origin + "/",
      "telephone": telephone,
      "email": email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": address.replace(/\n/g, ' '),
        "addressLocality": "İstanbul",
        "addressCountry": "TR"
      }
    };
    schemaScript.textContent = JSON.stringify(localBusinessSchema, null, 2);

  }, [settings, location]);

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 relative">
      <Navbar />
      <main className="flex-grow">
        <Outlet />
      </main>
      <Footer />

      {/* Floating Quick Contact Buttons */}
      {settings && settings.quick_contact && (
        <>
          {/* WhatsApp Button */}
          {settings.quick_contact.whatsapp_enabled && settings.quick_contact.whatsapp_number && (
            <a
              href={`https://wa.me/${settings.quick_contact.whatsapp_number.replace(/[^0-9]/g, '')}${
                settings.quick_contact.whatsapp_message 
                  ? `?text=${encodeURIComponent(settings.quick_contact.whatsapp_message)}` 
                  : ''
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="fixed z-[9990] w-14 h-14 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/20 flex items-center justify-center text-white border border-emerald-400 hover:scale-110 hover:-translate-y-0.5 active:scale-95 transition-all duration-300"
              style={{
                bottom: `${settings.quick_contact.whatsapp_offset_y || 20}px`,
                right: settings.quick_contact.whatsapp_position === 'bottom-right' ? '20px' : 'auto',
                left: settings.quick_contact.whatsapp_position === 'bottom-left' ? '20px' : 'auto',
              }}
              title="WhatsApp ile İletişime Geçin"
            >
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
                <path d="M12.012 2c-5.506 0-9.988 4.482-9.988 9.988 0 1.76.458 3.41 1.258 4.852l-1.282 4.678 4.792-1.256c1.402.76 2.99 1.196 4.678 1.196 5.508 0 9.988-4.48 9.988-9.988 0-5.506-4.48-9.988-9.988-9.988zm0 18.064c-1.536 0-2.97-.42-4.212-1.144l-.302-.18-2.834.744.756-2.756-.198-.316c-.792-1.264-1.21-2.724-1.21-4.248 0-4.394 3.576-7.97 7.97-7.97 4.394 0 7.97 3.576 7.97 7.97s-3.576 7.97-7.97 7.97zm4.398-6.002c-.24-.12-1.428-.704-1.65-.784-.22-.08-.382-.12-.542.12-.162.24-.624.784-.764.946-.142.162-.284.18-.524.06-.24-.12-1.016-.374-1.936-1.194-.716-.638-1.2-1.428-1.34-1.67-.142-.24-.016-.37.104-.49.108-.108.24-.28.36-.42.12-.14.162-.24.242-.4.08-.162.04-.302-.02-.422-.06-.12-.542-1.306-.744-1.792-.196-.474-.396-.41-.542-.418-.14-.008-.3-.008-.46-.008-.162 0-.426.06-.65.3-.224.24-.854.836-.854 2.036 0 1.202.874 2.36 1.0 2.52.124.162 1.72 2.628 4.17 3.684.582.25 1.036.4 1.39.514.586.186 1.118.16 1.54.098.47-.07 1.428-.584 1.63-.148.2-.436.2-.81.14-.87z"/>
              </svg>
            </a>
          )}

          {/* Phone Button */}
          {settings.quick_contact.phone_enabled && settings.quick_contact.phone_number && (
            <a
              href={`tel:${settings.quick_contact.phone_number.trim()}`}
              className="fixed z-[9990] w-14 h-14 rounded-full bg-blue-600 shadow-lg shadow-blue-600/20 flex items-center justify-center text-white border border-blue-500 hover:scale-110 hover:-translate-y-0.5 active:scale-95 transition-all duration-300"
              style={{
                bottom: `${settings.quick_contact.phone_offset_y || 90}px`,
                right: settings.quick_contact.phone_position === 'bottom-right' ? '20px' : 'auto',
                left: settings.quick_contact.phone_position === 'bottom-left' ? '20px' : 'auto',
              }}
              title="Bizi Telefonla Arayın"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-none stroke-current" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </a>
          )}
        </>
      )}

      {/* Announcement Popup Modal */}
      {showPopup && settings && settings.popup_settings && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative animate-scale-up">
            {/* Close Button */}
            <button 
              onClick={handleClosePopup}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center cursor-pointer transition z-50 border border-white/20"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header Image */}
            {settings.popup_settings.image_url && (
              <div className="h-48 w-full overflow-hidden bg-slate-100 relative">
                <img 
                  src={getImageUrl(settings.popup_settings.image_url)} 
                  alt="Duyuru" 
                  className="w-full h-full object-cover" 
                />
              </div>
            )}

            {/* Content */}
            <div className="p-6 space-y-4 text-center">
              {settings.popup_settings.title && (
                <h3 className="text-base font-extrabold text-slate-900 leading-snug">{settings.popup_settings.title}</h3>
              )}
              {settings.popup_settings.content && (
                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap max-h-36 overflow-y-auto">
                  {settings.popup_settings.content}
                </p>
              )}

              {/* Action Button */}
            {settings.popup_settings.link_text && settings.popup_settings.link_url && (
              <div className="pt-2">
                <a 
                  href={settings.popup_settings.link_url} 
                  onClick={handleClosePopup}
                  className="inline-block px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-600/20 transition duration-200 w-full text-center"
                >
                  {settings.popup_settings.link_text}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

      {/* Cookie Consent Banner */}
      {showCookieBanner && settings && settings.cookie_settings && settings.cookie_settings.enabled && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative animate-scale-up flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <span className="text-sm font-extrabold text-blue-600 tracking-tight">
                {settings.seo_settings?.title ? settings.seo_settings.title.split('|')[0].trim() : 'İSİLKOOP'}
              </span>
              <button 
                onClick={() => setShowCookieBanner(false)}
                className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center cursor-pointer transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-slate-100 px-5 pt-1">
              <button
                onClick={() => setCookieTab('settings')}
                className={`py-3 px-1.5 text-xs font-extrabold border-b-2 transition-all mr-6 ${
                  cookieTab === 'settings' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Ayarlarınızı Düzenleyin
              </button>
              <button
                onClick={() => setCookieTab('policy')}
                className={`py-3 px-1.5 text-xs font-extrabold border-b-2 transition-all ${
                  cookieTab === 'policy' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
              >
                Çerez Politikası
              </button>
            </div>

            {/* Tab Body Content */}
            <div className="p-6 overflow-y-auto max-h-[350px] space-y-4">
              {cookieTab === 'settings' ? (
                <>
                  {/* Onay ID Badge */}
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-xl p-3.5 text-white flex items-center gap-3 text-[10px] font-bold shadow-md shadow-blue-500/10 shrink-0">
                    <svg className="w-4 h-4 shrink-0 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                    <span className="truncate">Onay ID: {consentId}</span>
                  </div>

                  <p className="text-[11px] text-slate-500 leading-relaxed font-semibold">
                    Biz ve ortaklarımız, kişisel verileri işlemek (örneğin benzersiz tanımlayıcılar gibi çerez verileri) ve web sitesi deneyimini optimize etmek için cihazınızda bilgi saklarız veya bu bilgilere erişiriz.
                  </p>

                  {/* Cookie Toggles */}
                  <div className="space-y-3 pt-1">
                    {/* Necessary */}
                    <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1">&rsaquo; Gerekli Çerezler</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" disabled checked className="sr-only peer" />
                          <div className="w-8 h-4.5 bg-blue-600/50 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:translate-x-3.5"></div>
                        </label>
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold leading-relaxed">Web sitemizin çalışması ve güvenli gezinmeniz için zorunlu olan çerezlerdir.</p>
                    </div>

                    {/* Analytical */}
                    <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1">&rsaquo; Analitik Çerezler</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={cookiePreferences.analytical} 
                            onChange={(e) => setCookiePreferences(prev => ({ ...prev, analytical: e.target.checked }))}
                            className="sr-only peer" 
                          />
                          <div className="w-8 h-4.5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-3.5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                        {settings.cookie_settings.analytics_cookies_desc || "Web sitemizin performansını ölçmek ve ziyaretçi istatistiklerini takip etmek için kullanılır."}
                      </p>
                    </div>

                    {/* Marketing */}
                    <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/50 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1">&rsaquo; Pazarlama Çerezleri</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={cookiePreferences.marketing} 
                            onChange={(e) => setCookiePreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                            className="sr-only peer" 
                          />
                          <div className="w-8 h-4.5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-3.5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                        {settings.cookie_settings.marketing_cookies_desc || "Size ilgi alanlarınıza özel pazarlama reklamları sunabilmek için kullanılır."}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4 text-xs text-slate-600 leading-relaxed text-left py-2 font-medium">
                  <p>
                    6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) uyarınca kişisel verilerinizin toplanması, işlenmesi ve korunması ile ilgili detaylı bilgilere resmi politikamız üzerinden erişebilirsiniz.
                  </p>
                  {settings.cookie_settings.cookie_policy_url && (
                    <div className="pt-2">
                      <a 
                        href={settings.cookie_settings.cookie_policy_url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-extrabold border-b border-blue-600/30 pb-0.5"
                      >
                        Çerez Politikası ve Aydınlatma Metnini İncele &rarr;
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions Footer */}
            {cookieTab === 'settings' && (
              <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={handleRejectAllCookies}
                  className="flex-1 py-3 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition"
                >
                  Tümünü Reddet
                </button>
                <button
                  onClick={handleAcceptAllCookies}
                  className="flex-1 py-3 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition"
                >
                  Tümünü Kabul Et
                </button>
                <button
                  onClick={handleSaveCookiePreferences}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-md shadow-blue-600/10"
                >
                  Tercihlerimi Kaydet
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Persistent Cookie Revision Button */}
      {settings && settings.cookie_settings && settings.cookie_settings.enabled && !showCookieBanner && (
        <button
          onClick={() => setShowCookieBanner(true)}
          className="fixed z-[9980] bottom-5 left-5 w-10 h-10 rounded-full bg-slate-900 shadow-lg hover:bg-slate-800 text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 border border-slate-800 cursor-pointer"
          title="Çerez Tercihlerini Düzenle"
        >
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 4.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S13.33 8 12.5 8 11 7.33 11 6.5zm-5 7c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S8.33 15 7.5 15 6 14.33 6 13.5zm3-4c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S11.33 11 10.5 11 9 10.33 9 9.5zm5 7c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-1.5.67-1.5 1.5-.67-1.5-1.5-1.5zm3-4c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S19.33 14 18.5 14 17 13.33 17 12.5z"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default Layout;
